
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('firstName', formData.firstName);
      formBody.append('lastName', formData.lastName);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5k92hnv8jsbcnoi0gv0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-16 md:py-32 px-4 md:px-8 lg:px-20 bg-gradient-to-b from-emerald-50 to-white">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl md:rounded-[32px] shadow-2xl p-6 md:p-12 lg:p-20">
          <div className="text-center mb-12 md:mb-16">
            <div className="flex justify-center mb-4">
              <i className="ri-mail-line text-4xl md:text-5xl text-[#B8E986]"></i>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-base md:text-lg text-gray-600">Have questions about Leachianus care? We're here to help.</p>
          </div>

          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 md:px-5 py-3 md:py-4 border-2 border-gray-200 rounded-xl focus:border-[#B8E986] focus:outline-none transition-colors"
                  placeholder="John"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 md:px-5 py-3 md:py-4 border-2 border-gray-200 rounded-xl focus:border-[#B8E986] focus:outline-none transition-colors"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 md:px-5 py-3 md:py-4 border-2 border-gray-200 rounded-xl focus:border-[#B8E986] focus:outline-none transition-colors"
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-4 md:px-5 py-3 md:py-4 border-2 border-gray-200 rounded-xl focus:border-[#B8E986] focus:outline-none transition-colors cursor-pointer"
              >
                <option value="">Select a topic</option>
                <option value="care-questions">Care Questions</option>
                <option value="breeding-info">Breeding Information</option>
                <option value="health-concerns">Health Concerns</option>
                <option value="purchasing">Purchasing Inquiry</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 md:px-5 py-3 md:py-4 border-2 border-gray-200 rounded-xl focus:border-[#B8E986] focus:outline-none transition-colors resize-none"
                placeholder="Tell us about your question or inquiry..."
              />
              <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500 characters</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#B8E986] text-gray-900 py-3 md:py-4 rounded-xl text-base md:text-lg font-semibold hover:bg-[#a8d976] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
                <p className="text-green-800">Thank you! Your message has been sent successfully.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                <i className="ri-error-warning-fill text-red-600 text-2xl"></i>
                <p className="text-red-800">Sorry, there was an error sending your message. Please try again.</p>
              </div>
            )}
          </form>

          <div className="mt-6 md:mt-8 text-center text-sm text-gray-500">
            We typically respond within 24 hours
          </div>

          <div className="flex justify-center gap-3 md:gap-4 mt-6 md:mt-8">
            <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#B8E986] transition-colors cursor-pointer">
              <i className="ri-facebook-fill text-gray-700 text-lg"></i>
            </a>
            <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#B8E986] transition-colors cursor-pointer">
              <i className="ri-instagram-line text-gray-700 text-lg"></i>
            </a>
            <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#B8E986] transition-colors cursor-pointer">
              <i className="ri-youtube-fill text-gray-700 text-lg"></i>
            </a>
            <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#B8E986] transition-colors cursor-pointer">
              <i className="ri-discord-fill text-gray-700 text-lg"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
